import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts'
import type { PerUkeResponse } from './api'

interface Props {
  data: PerUkeResponse | null
  loading: boolean
}

/** Y-axis perspective: raw weekly counts, or each series as % of its own total. */
export type DisplayMode = 'antall' | 'andel'

// Fixed palette cycled by series index — keeps colors stable across renders.
const PALETTE = [
  '#3B82F6', '#EF4444', '#10B981', '#F59E0B', '#8B5CF6',
  '#EC4899', '#14B8A6', '#F97316', '#6366F1', '#84CC16',
]

/** Series label: bare merke when no klasse split, else "merke · klasse". */
function seriesLabel(merke: string | null, klasse: string | null): string {
  const m = merke ?? '—'
  return klasse == null ? m : `${m} · ${klasse}`
}

/**
 * Multi-line eierskifter-per-week trend. One line per merke × overdragelses-
 * klasse pair. The Antall / Andel toggle switches the Y-axis between raw
 * weekly counts and each series normalised to a share of its own window total
 * — so brands of very different volume can still be compared by shape.
 */
export default function PerUkeChart({ data, loading }: Props) {
  const { t } = useTranslation('bruktbil')
  const [mode, setMode] = useState<DisplayMode>('antall')

  if (loading) {
    return (
      <div
        data-testid="per-uke-chart"
        className="card p-4 animate-pulse h-52 bg-gray-100 dark:bg-dark-border rounded-sm"
      />
    )
  }

  if (data && data.rows.length === 0) {
    return (
      <div data-testid="per-uke-chart" className="card p-4 text-sm text-gray-500">
        {t('merkeModell.perUke.ingenData')}
      </div>
    )
  }

  if (!data) return null

  // Per-series totals over the window — the denominator for "andel" mode.
  const seriesTotals = new Map<string, number>()
  for (const r of data.rows) {
    const s = seriesLabel(r.merke, r.klasse)
    seriesTotals.set(s, (seriesTotals.get(s) ?? 0) + r.count)
  }

  // Pivot rows into { week, [series]: value }[] keyed by week. In "andel" mode
  // the value is the series' percentage share of its own total.
  const seriesSet = new Set<string>()
  const byWeek = new Map<string, Record<string, number | string>>()
  for (const r of data.rows) {
    const series = seriesLabel(r.merke, r.klasse)
    seriesSet.add(series)
    let entry = byWeek.get(r.week)
    if (!entry) {
      entry = { week: r.week }
      byWeek.set(r.week, entry)
    }
    if (mode === 'andel') {
      const tot = seriesTotals.get(series) ?? 0
      entry[series] = tot > 0 ? Math.round((r.count / tot) * 1000) / 10 : 0
    } else {
      entry[series] = r.count
    }
  }
  const chartData = Array.from(byWeek.values()).sort((a, b) =>
    String(a.week).localeCompare(String(b.week)),
  )
  const seriesList = Array.from(seriesSet)

  return (
    <div data-testid="per-uke-chart" className="card p-4">
      <div className="flex items-center justify-between gap-4 flex-wrap mb-2">
        <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {t('merkeModell.perUke.title')}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500">{t('merkeModell.perUke.yAkse')}</span>
          <div className="flex rounded-lg border border-gray-200 dark:border-dark-border overflow-hidden">
            {(['antall', 'andel'] as DisplayMode[]).map(m => (
              <button
                key={m}
                type="button"
                onClick={() => setMode(m)}
                data-mode={m}
                className={`px-3 py-1 text-xs font-medium transition-colors ${
                  mode === m
                    ? 'bg-primary-500 text-white'
                    : 'bg-white dark:bg-dark-card text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-dark-hover'
                }`}
              >
                {m === 'antall' ? t('common.antall') : t('merkeModell.perUke.andel')}
              </button>
            ))}
          </div>
        </div>
      </div>
      {data.total_series > seriesList.length && (
        <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
          {t('merkeModell.perUke.viserSerier', { shown: seriesList.length, total: data.total_series })}
        </p>
      )}
      <ResponsiveContainer width="100%" height={300}>
        <LineChart data={chartData} margin={{ top: 4, right: 8, left: 0, bottom: 4 }}>
          <CartesianGrid strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="week" tick={{ fontSize: 11 }} />
          <YAxis
            tick={{ fontSize: 11 }}
            tickFormatter={mode === 'andel' ? (v: number) => `${v} %` : undefined}
          />
          <Tooltip
            formatter={(v: number) =>
              mode === 'andel'
                ? `${v.toLocaleString('nb-NO')} %`
                : v.toLocaleString('nb-NO')
            }
          />
          <Legend />
          {seriesList.map((s, i) => (
            <Line
              key={s}
              type="monotone"
              dataKey={s}
              stroke={PALETTE[i % PALETTE.length]}
              strokeWidth={2}
              dot={false}
              connectNulls
            />
          ))}
        </LineChart>
      </ResponsiveContainer>
    </div>
  )
}
